import React from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import request from "../../../utilities/requests";
import { toast } from "../../../utilities/SnackBarUtils";

const REASONS = ["harassment", "offensive", "spam", "other"];
const MAX_DETAILS = 500;

const ReportDialog = ({ open, threadId, name, onClose, onReported }) => {
  const { t } = useTranslation();
  const [reason, setReason] = React.useState("");
  const [details, setDetails] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setReason("");
      setDetails("");
    }
  }, [open]);

  const submit = async () => {
    if (!reason || busy) return;
    setBusy(true);
    const result = await request("eventchat/report", "post", {
      threadId,
      reason,
      details: details.trim(),
    });
    setBusy(false);
    if (!result?.success) {
      toast.error(result?.error || t("chat.error.generic"));
      return;
    }
    toast.success(t("chat.report.done"));
    onReported();
  };

  return (
    <Dialog
      open={open}
      onClose={() => !busy && onClose()}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: "24px" } }}
    >
      <DialogTitle>{t("chat.report.title", { name })}</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 1 }}>
          {t("chat.report.body", { name })}
        </Typography>
        <RadioGroup value={reason} onChange={(e) => setReason(e.target.value)}>
          {REASONS.map((value) => (
            <FormControlLabel
              key={value}
              value={value}
              control={<Radio />}
              label={t(`chat.report.reason.${value}`)}
            />
          ))}
        </RadioGroup>
        <TextField
          multiline
          minRows={2}
          maxRows={4}
          fullWidth
          size="small"
          sx={{
            mt: 1,
            "& .MuiOutlinedInput-root": {
              borderRadius: "20px",
              paddingLeft: "18px",
              paddingRight: "18px",
            },
            "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
              borderWidth: "1px",
            },
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
              {
                borderWidth: "1px",
              },
          }}
          placeholder={t("chat.report.details")}
          value={details}
          inputProps={{ maxLength: MAX_DETAILS }}
          onChange={(e) => setDetails(e.target.value)}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          className="tnfm-pill-btn"
          style={{ minHeight: 32, paddingLeft: 16, paddingRight: 16 }}
          onClick={onClose}
          disabled={busy}
        >
          {t("chat.cancel")}
        </Button>
        <Button
          className="tnfm-pill-btn"
          style={{ minHeight: 32, paddingLeft: 24, paddingRight: 24 }}
          color="error"
          variant="contained"
          disabled={!reason || busy}
          onClick={submit}
        >
          {busy ? <CircularProgress size={20} /> : t("chat.report.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReportDialog;
